/**
 * User polls (betup-server-analytics `/api/dashboard/polls`). A poll is sent by push to users
 * with given user tags; each user answers with an option or a custom text, or dismisses it.
 * Texts are localized per ISO 639-1 code and served by the user's profile language.
 */
import { UserTagCombinationMode } from '../UserTagCombinationMode';

/** Languages offered in the editor; the players' app is localized into these. */
export const POLL_LANGUAGES = ['en', 'ru', 'ro', 'es', 'it', 'pt', 'de', 'fr', 'tr', 'uk', 'bg', 'hi', 'ar'] as const;

export const POLL_LANGUAGE_LABELS: Record<string, string> = {
    en: 'English', ru: 'Russian', ro: 'Romanian', es: 'Spanish', it: 'Italian', pt: 'Portuguese', de: 'German',
    fr: 'French', tr: 'Turkish', uk: 'Ukrainian', bg: 'Bulgarian', hi: 'Hindi', ar: 'Arabic',
};

export type PollStatus = 'DRAFT' | 'ACTIVE' | 'CLOSED';
export type PollSendStatus = 'PENDING' | 'SENDING' | 'COMPLETED' | 'FAILED';
export type PollAnswerStatus = 'SENT' | 'OPENED' | 'DISMISSED' | 'ANSWERED';
export type PollAnswerFilter = 'RESPONSES' | 'ANSWERED' | 'OPTION' | 'CUSTOM' | 'DISMISSED' | 'PENDING' | 'ALL';

export interface PollTexts {
    question?: string | null;
    pushTitle?: string | null;
    pushBody?: string | null;
    customAnswerHint?: string | null;
    thankYouMessage?: string | null;
}

export interface PollOptionDto {
    /** Blank for new options; the server assigns a stable key. */
    key?: string | null;
    texts: Record<string, string>;
}

export interface PollSummaryDto {
    targeted: number;
    opened: number;
    answered: number;
    dismissed: number;
}

export interface PollDto {
    id: string;
    name: string;
    status: PollStatus;
    defaultLanguage: string;
    texts: Record<string, PollTexts>;
    options: PollOptionDto[];
    allowCustomAnswer: boolean;
    customAnswerMaxLength: number;
    sendsCount: number;
    lastSentAt?: string | null;
    closedAt?: string | null;
    createdAt: string;
    updatedAt?: string | null;
    summary?: PollSummaryDto | null;
}

export interface PollUpsertRequest {
    name: string;
    defaultLanguage: string;
    texts: Record<string, PollTexts>;
    options: PollOptionDto[];
    allowCustomAnswer: boolean;
    customAnswerMaxLength: number;
}

export interface PollAudienceRequest {
    userTags: string[];
    tagCombinationMode: UserTagCombinationMode;
    activeWithinDays?: number | null;
}

export interface PollLanguageCountDto {
    lang?: string | null;
    count: number;
    servedLanguage?: string | null;
}

export interface PollAudienceDto {
    totalUsers: number;
    languages: PollLanguageCountDto[];
    alreadyAnswered?: number | null;
}

export interface PollSendRequest extends PollAudienceRequest {
    skipDismissed: boolean;
    maxUsers?: number | null;
}

export interface PollSendDto {
    id: string;
    pollId: string;
    pollName?: string | null;
    userTags: string[];
    tagCombinationMode: UserTagCombinationMode;
    activeWithinDays?: number | null;
    skipDismissed: boolean;
    maxUsers?: number | null;
    test: boolean;
    testUserIds: number[];
    status: PollSendStatus;
    matchedCount: number;
    targetedCount: number;
    skippedAnsweredCount: number;
    skippedDismissedCount: number;
    pushedCount: number;
    failedCount: number;
    languageBreakdown: Record<string, number>;
    error?: string | null;
    createdAt: string;
    startedAt?: string | null;
    completedAt?: string | null;
    summary?: PollSummaryDto | null;
}

export interface PollPageDto<T> {
    items: T[];
    totalItems: number;
}

export interface PollOptionStatDto {
    key: string;
    text: string;
    exists: boolean;
    count: number;
}

export interface PollLanguageStatDto {
    lang: string;
    targeted: number;
    answered: number;
    dismissed: number;
}

export interface PollDailyStatDto {
    day: string;
    answered: number;
    dismissed: number;
}

export interface PollStatsDto {
    pollId: string;
    sendId?: string | null;
    targeted: number;
    pending: number;
    opened: number;
    answered: number;
    dismissed: number;
    customAnswers: number;
    responseRate: number;
    answerRate: number;
    options: PollOptionStatDto[];
    languages: PollLanguageStatDto[];
    daily: PollDailyStatDto[];
}

export interface PollAnswerRowDto {
    id: string;
    userRef: number;
    userName?: string | null;
    sendId?: string | null;
    status: PollAnswerStatus;
    optionKey?: string | null;
    optionText?: string | null;
    customText?: string | null;
    lang?: string | null;
    osType?: string | null;
    buildNumber?: number | null;
    test: boolean;
    dismissCount: number;
    sentAt?: string | null;
    openedAt?: string | null;
    dismissedAt?: string | null;
    answeredAt?: string | null;
}

export interface PollLocalizationGenerateRequest {
    texts: PollTexts;
    options: PollOptionDto[];
    supportedLanguages: string[];
    improveEnglish: boolean;
}

export interface PollLocalizationGenerateResponse {
    texts: Record<string, PollTexts>;
    options: PollOptionDto[];
}

export type PollMutationResult<T> = { ok: true; data: T } | { ok: false; error: string };
